/**
 * Open source contribution types - classifies the nature of each contribution
 */
export const ContributionType = {
  Feature: "Feature",
  Bugfix: "Bugfix",
  Docs: "Docs",
  CI: "CI",
  Tests: "Tests",
  Security: "Security",
  Accessibility: "Accessibility",
  Refactoring: "Refactoring",
  Performance: "Performance",
} as const;
export type ContributionType =
  (typeof ContributionType)[keyof typeof ContributionType];

export const CONTRIBUTION_TYPES = Object.values(
  ContributionType,
) as ContributionType[];

export const ContributionChangeRequestKind = {
  PullRequest: "pull-request",
  MergeRequest: "merge-request",
} as const;
export type ContributionChangeRequestKind =
  (typeof ContributionChangeRequestKind)[keyof typeof ContributionChangeRequestKind];

/**
 * A single commit in a contribution
 */
type Commit = {
  sha: string;
  title: string;
  url: string;
};

/**
 * A linked issue that was fixed by a contribution
 */
type LinkedIssue = {
  /** Issue number */
  number: number;
  /** Issue title */
  title: string;
  /** Number of days the issue was open before being fixed */
  daysOpen: number;
  /** GitHub issue URL */
  url: string;
};

/**
 * A linked pull request or merge request related to a contribution
 */
type LinkedChangeRequest = {
  /** Pull-request or merge-request number (e.g. 24775) */
  number: number;
  kind: ContributionChangeRequestKind;
  /** Pull-request or merge-request URL */
  url: string;
};

/**
 * Represents a contribution (one or more commits) to a repository
 */
export type Contribution = {
  description: string;
  types: ContributionType[];
  commits: Commit[];
  /** Issues that were fixed by this contribution */
  issuesFixed?: LinkedIssue[];
  /** Pull requests or merge requests related to this contribution */
  changeRequests?: LinkedChangeRequest[];
  /** Skills demonstrated in this specific contribution */
  skills?: SkillTag[];
};

/**
 * Skills demonstrated in a contribution
 */
export const SkillTag = {
  // Programming languages
  TypeScript: "TypeScript",
  JavaScript: "JavaScript",
  Python: "Python",
  Java: "Java",
  Go: "Go",
  Haskell: "Haskell",
  C: "C",
  Groovy: "Groovy",

  // Frameworks/Libraries
  React: "React",
  NextJS: "Next.js",
  Angular: "Angular",
  Django: "Django",
  Spring: "Spring",
  JavaFX: "JavaFX",
  NativeScript: "NativeScript",

  // Testing
  UnitTesting: "Unit Testing",
  E2ETesting: "E2E Testing",
  TestContainers: "Testcontainers",

  // DevOps/CI
  Docker: "Docker",
  CI_CD: "CI/CD",
  GitHubActions: "GitHub Actions",
  TravisCI: "Travis CI",
  Gradle: "Gradle",
  Maven: "Maven",

  // Domains
  Accessibility: "Accessibility",
  Security: "Security",
  Performance: "Performance",
  API_Design: "API Design",
  Documentation: "Documentation",
  CodeQuality: "Code Quality",
  TypeSafety: "Type Safety",
  ErrorHandling: "Error Handling",
  IoT: "IoT/Home Automation",
  Database: "Database",
  AI_LLM: "AI/LLM",
} as const;
export type SkillTag = (typeof SkillTag)[keyof typeof SkillTag];

/**
 * Time period for download statistics
 */
export const DownloadPeriod = {
  Weekly: "weekly",
  Monthly: "monthly",
  Yearly: "yearly",
  AllTime: "all-time",
} as const;
export type DownloadPeriod =
  (typeof DownloadPeriod)[keyof typeof DownloadPeriod];

export const PACKAGE_REGISTRIES = ["npm", "pypi", "maven"] as const;
type PackageRegistry = (typeof PACKAGE_REGISTRIES)[number];

/**
 * Download statistics for a package
 */
type PackageDownloads = {
  /** Package registry (npm, pypi, maven) */
  registry: PackageRegistry;
  /** Package name */
  packageName: string;
  /** Download count */
  downloads: number;
  /** Time period for the download count */
  period: DownloadPeriod;
  /** Date when the download count was retrieved */
  asOf: string;
};

/**
 * A repository with open source contributions
 */
export type Repository = {
  /** Unique identifier for the repository (used for grouping) */
  id: string;
  /** Display name in format owner/repo */
  name: string;
  /** GitHub URL */
  url: string;
  /** Star count (see METRICS_LAST_UPDATED_DATE for when counters were last refreshed) */
  stars: number;
  /** Technologies used by the repository (not necessarily the commit) */
  technologies: string[];
  /** Years in which contributions were made */
  contributionYears: number[];
  /** List of contributions */
  contributions: Contribution[];
  /**
   * Optional: ID of a related repository whose contributions should be
   * grouped together (e.g., home-assistant/home-assistant.io with home-assistant/core)
   */
  relatedRepositoryId?: string;
  /** Optional: Package download statistics */
  downloads?: PackageDownloads;
  /** Skills demonstrated in contributions */
  skills?: SkillTag[];
};

/**
 * Metrics data date - when stars, followers, downloads, and Docker pull counters were last updated
 */
export const METRICS_LAST_UPDATED_DATE = "2026-09-01";
